import React, { useState } from "react";
import { inferFieldType, parseEditableValue, serializeValue } from "../utils/firestoreValueUtils";

export default function DocumentEditorModal({ row, collectionName, onClose, onSave }) {
  const [fields, setFields] = useState(() => ({ ...row.data }));
  const [view, setView] = useState("form");
  const [newKey, setNewKey] = useState("");
  const [newVal, setNewVal] = useState("");
  const [saving, setSaving] = useState(false);

  const updateField = (key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  };

  const removeField = (key) => {
    setFields((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(row.id, fields, row.data);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fa-modal-backdrop" onClick={onClose}>
      <div className="fa-modal" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-slate-200 p-4">
          <h2 className="text-lg font-bold">Edit document</h2>
          <p className="text-sm text-slate-500">
            {collectionName} / <code>{row.id}</code>
          </p>
          <div className="mt-2 flex gap-2">
            <button type="button" className={`fa-btn${view === "form" ? " fa-btn-primary" : ""}`} onClick={() => setView("form")}>
              Form
            </button>
            <button type="button" className={`fa-btn${view === "json" ? " fa-btn-primary" : ""}`} onClick={() => setView("json")}>
              JSON
            </button>
            <button type="button" className={`fa-btn${view === "tree" ? " fa-btn-primary" : ""}`} onClick={() => setView("tree")}>
              Tree
            </button>
          </div>
        </div>
        <div className="max-h-[50vh] overflow-auto p-4">
          {view === "json" && (
            <textarea
              className="h-64 w-full rounded-lg border border-slate-300 p-2 font-mono text-xs"
              value={JSON.stringify(serializeValue(fields), null, 2)}
              onChange={(e) => {
                try {
                  setFields(JSON.parse(e.target.value));
                } catch {
                  /* ignore parse while typing */
                }
              }}
            />
          )}
          {view === "tree" && (
            <pre className="rounded-lg bg-slate-100 p-3 text-xs dark:bg-slate-800">
              {JSON.stringify(serializeValue(fields), null, 2)}
            </pre>
          )}
          {view === "form" && (
            <div className="space-y-3">
              {Object.entries(fields).map(([key, value]) => {
                const type = inferFieldType(value);
                return (
                  <div key={key} className="grid gap-2 sm:grid-cols-[140px_1fr_auto]">
                    <label className="text-xs font-semibold text-slate-600">{key}</label>
                    <input
                      className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                      value={typeof value === "object" ? JSON.stringify(serializeValue(value)) : String(value ?? "")}
                      onChange={(e) => {
                        try {
                          updateField(key, parseEditableValue(e.target.value, type));
                        } catch {
                          updateField(key, e.target.value);
                        }
                      }}
                    />
                    <button type="button" className="fa-btn fa-btn-danger" onClick={() => removeField(key)}>
                      Remove
                    </button>
                  </div>
                );
              })}
              <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-3">
                <input
                  className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  placeholder="New field"
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                />
                <input
                  className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm"
                  placeholder="Value"
                  value={newVal}
                  onChange={(e) => setNewVal(e.target.value)}
                />
                <button
                  type="button"
                  className="fa-btn"
                  onClick={() => {
                    if (!newKey.trim()) return;
                    updateField(newKey.trim(), newVal);
                    setNewKey("");
                    setNewVal("");
                  }}
                >
                  Add field
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 p-4">
          <button type="button" className="fa-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="fa-btn fa-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
